import { addFabricEventListener, dispatchFabricEvent } from '../../../hooks/useFabricEvent.js';
import { PluginKey, Plugin } from '@tiptap/pm/state';
import { DecorationSet } from '@tiptap/pm/view';
import { scrollToRangeInDoc } from '../utils.js';
import { AnnotationState } from './AnnotationState.js';
import { AnnotationMeta } from './AnnotationMeta.js';
import { getCombinedRect } from '@fabric-app/utils/element';
import { isString } from '@fabric-app/utils/guards';
import { getFbcDocumentResourceId } from '../FbcDocument.js';
import { FloatingActions } from '../../../annotation/components/FloatingActions.js';
import { setIsCommentOrphan } from '../../../comments/stores/orphanCommentsStore.js';
import { ANNOTATION_DEFAULT_COLOR } from '../../../annotation/config.js';

const AnnotationPluginKey = new PluginKey("annotation");
const findAnnotationPosition = (doc, filter) => {
  let found = false;
  let start = Infinity;
  let end = 0;
  let annotationId = null;
  let commentId = null;
  let color = null;
  let text = null;
  const searchParam = "annotationId" in filter ? "annotationId" : "commentId";
  const searchValue = "annotationId" in filter ? filter.annotationId : filter.commentId;
  doc.descendants((node, pos) => {
    if (!node.isText || !node.isLeaf) return true;
    const mark = node.marks.find((mark2) => mark2.type.name === "annotation" && mark2.attrs[searchParam] === searchValue);
    if (mark) {
      start = Math.min(pos, start);
      end = Math.max(pos + node.nodeSize, end);
      found = true;
      annotationId = mark.attrs.annotationId;
      commentId = mark.attrs.commentId;
      color = mark.attrs.color;
      text = node.text ? node.text : node.textBetween(0, node.nodeSize, "...");
    }
    return true;
  });
  if (!found) return null;
  return {
    start,
    end,
    annotationId,
    commentId,
    color,
    text
  };
};
const AnnotationPlugin = (editor) => {
  let hoverTimeout = null;
  const resetAnnotationHover = (view, dispatch = true) => {
    if (hoverTimeout) return;
    const pluginState = AnnotationPluginKey.getState(view.state);
    if (!pluginState?.hoveredAnnotationId) return;
    const resourceId = getFbcDocumentResourceId(view.state);
    hoverTimeout = window.setTimeout(() => {
      AnnotationMeta.setAnnotationState(view, null, null, null);
      if (dispatch && resourceId) dispatchFabricEvent("fabric-annotation-action-hover", {
        type: "hover-annotation",
        hover: null,
        origin: "content",
        resourceId
      });
      hoverTimeout = null;
    }, 1e3);
  };
  const setAnnotationHover = (view, annotationId, commentId, dispatch = true) => {
    if (hoverTimeout) window.clearTimeout(hoverTimeout);
    hoverTimeout = null;
    const pluginState = AnnotationPluginKey.getState(view.state);
    if (pluginState?.hoveredAnnotationId === annotationId) return;
    const resourceId = getFbcDocumentResourceId(view.state);
    AnnotationMeta.setAnnotationState(view, annotationId, null, commentId);
    if (commentId && dispatch && resourceId) {
      dispatchFabricEvent("fabric-annotation-action-hover", {
        type: "hover-annotation",
        hover: {
          annotationId,
          commentId
        },
        origin: "content",
        resourceId
      });
    }
  };
  return new Plugin({
    key: AnnotationPluginKey,
    view(editorView) {
      const annotationMark = editorView.state.schema.marks.annotation;
      let domView = null;
      const unsubLink = addFabricEventListener("fabric-annotation-action-link", (e) => {
        const { annotation, commentId, resourceId } = e.detail;
        const stateResourceId = getFbcDocumentResourceId(editorView.state);
        if (stateResourceId !== resourceId) return;
        const id = annotation.id || annotation.notepad?.elementId;
        if (!id) return;
        const pos = findAnnotationPosition(editorView.state.doc, {
          annotationId: id
        });
        if (!pos) return;
        const tr = editorView.state.tr;
        tr.removeMark(pos.start, pos.end, annotationMark);
        tr.addMark(pos.start, pos.end, annotationMark.create({
          annotationId: id,
          commentId,
          color: pos.color ?? ANNOTATION_DEFAULT_COLOR
        }));
        editorView.dispatch(tr);
      });
      const unsubFocus = addFabricEventListener("fabric-annotation-action-focus", (e) => {
        const { commentId, origin, annotation, resourceId } = e.detail;
        const stateResourceId = getFbcDocumentResourceId(editorView.state);
        if (stateResourceId !== resourceId || !commentId) return;
        if (origin === "content" || isString(annotation) || !annotation.notepad) return;
        const pos = findAnnotationPosition(editorView.state.doc, {
          commentId
        });
        if (!pos) return;
        const annotationId = annotation.id ?? annotation.notepad?.elementId ?? null;
        AnnotationMeta.setAnnotationState(editorView, annotationId, null, commentId);
        requestAnimationFrame(() => {
          AnnotationMeta.setAnnotationState(editorView, annotationId, annotationId, commentId);
        });
        const element = document.querySelector(`[data-annotation-id="${annotationId}"],[data-comment-id="${commentId}"]`);
        if (element) element.scrollIntoView({
          behavior: "instant",
          block: "center"
        });
        else scrollToRangeInDoc(editorView, pos.start, pos.end);
      });
      const unsubHover = addFabricEventListener("fabric-annotation-action-hover", (e) => {
        const { hover, origin, resourceId } = e.detail;
        const stateResourceId = getFbcDocumentResourceId(editorView.state);
        if (stateResourceId !== resourceId) return;
        if (origin === "content") return;
        if (hover === null || !hover.annotationId) {
          resetAnnotationHover(editorView, false);
        } else if (hover && hover.annotationId) {
          setAnnotationHover(editorView, hover.annotationId, hover.commentId, false);
        }
      });
      const unsubDelete = addFabricEventListener("fabric-annotation-action-delete", (e) => {
        const { commentId, annotationId, resourceId } = e.detail;
        const stateResourceId = getFbcDocumentResourceId(editorView.state);
        if (stateResourceId !== resourceId || !commentId && !annotationId) return;
        const result = findAnnotationPosition(editorView.state.doc, annotationId ? {
          annotationId
        } : {
          commentId
        });
        if (!result) return;
        const tr = editorView.state.tr;
        tr.removeMark(result.start, result.end, annotationMark);
        editorView.dispatch(tr);
      });
      const unsubOrphanCheck = addFabricEventListener("fabric-annotation-action-check-orphans", (e) => {
        const { commentIds, resourceId } = e.detail;
        const stateResourceId = getFbcDocumentResourceId(editorView.state);
        if (stateResourceId !== resourceId) return;
        for (const commentId of commentIds) {
          const result = findAnnotationPosition(editorView.state.doc, {
            commentId
          });
          setIsCommentOrphan(commentId, resourceId, !result);
        }
      });
      const getDomView = () => {
        if (domView) return domView;
        const parent = editorView.dom.parentElement;
        if (!parent) return null;
        domView = document.createElement("div");
        domView.innerHTML = FloatingActions.Render;
        const commentButton = domView.querySelector('[data-annotation-action="comment"]');
        const deleteButton = domView.querySelector('[data-annotation-action="delete"]');
        if (!commentButton || !deleteButton) return null;
        parent.appendChild(domView);
        commentButton.addEventListener("click", () => {
          const pluginState = AnnotationPluginKey.getState(editorView.state);
          if (!pluginState || !pluginState.hoveredAnnotationId) return;
          const resourceId = getFbcDocumentResourceId(editorView.state);
          if (!resourceId) return;
          const pos = findAnnotationPosition(editorView.state.doc, {
            annotationId: pluginState.hoveredAnnotationId
          });
          if (!pos) return;
          dispatchFabricEvent("fabric-annotation-action-focus", {
            type: "focus-annotation",
            commentId: pluginState.commentId ?? void 0,
            annotation: {
              id: pluginState.hoveredAnnotationId,
              notepad: {
                elementId: pluginState.hoveredAnnotationId,
                text: pos.text ?? "",
                color: pos.color ?? ANNOTATION_DEFAULT_COLOR
              }
            },
            origin: "content",
            resourceId
          });
        });
        deleteButton.addEventListener("click", () => {
          if (!domView) return;
          const annotationId = domView.dataset.annotationId;
          if (!annotationId) return;
          editor.chain().focus().deleteAnnotation(annotationId).run();
        });
        domView.addEventListener("mouseenter", () => {
          if (!domView) return;
          const annotationId = domView.dataset.annotationId;
          const commentId = domView.dataset.commentId;
          if (!annotationId || !commentId) return;
          setAnnotationHover(editorView, annotationId, commentId, false);
        });
        domView.addEventListener("mouseleave", () => {
          resetAnnotationHover(editorView, false);
        });
        return domView;
      };
      return {
        update: (editorView2) => {
          const state = AnnotationPluginKey.getState(editorView2.state);
          if (!state || state.hoveredAnnotationId === null || !state.annotationRange) {
            if (domView) {
              domView.remove();
              domView = null;
            }
            return;
          }
          const elements = editorView2.dom.querySelectorAll("[data-annotation-hovered]");
          const combinedRects = getCombinedRect(Array.from(elements));
          if (!combinedRects) {
            if (domView) {
              domView.remove();
              domView = null;
            }
            return;
          }
          const dom = getDomView();
          if (!dom) return;
          dom.style.position = "fixed";
          dom.style.left = `${combinedRects.left + combinedRects.width / 2}px`;
          dom.style.top = `${combinedRects.top}px`;
          dom.style.transform = "translate(-50%, calc(-100% - 20px))";
          dom.style.zIndex = "125";
          dom.dataset.annotationActions = "true";
          dom.dataset.annotationId = state.hoveredAnnotationId;
          dom.dataset.commentId = state.commentId ?? void 0;
        },
        destroy: () => {
          unsubLink();
          unsubFocus();
          unsubHover();
          unsubDelete();
          unsubOrphanCheck();
          if (domView) {
            domView.remove();
            domView = null;
          }
        }
      };
    },
    state: {
      init: () => AnnotationState.empty,
      apply: (tr, state) => {
        return state.handleSelection(tr).handleMeta(tr).updateDecorations(tr);
      }
    },
    props: {
      decorations: (state) => {
        const pluginState = AnnotationPluginKey.getState(state);
        return pluginState?.decorationSet ?? DecorationSet.empty;
      },
      handleDOMEvents: {
        pointermove: (view, event) => {
          const annotationId = event.target instanceof HTMLElement && event.target.getAttribute("data-annotation-id");
          const commentId = event.target instanceof HTMLElement && event.target.getAttribute("data-comment-id") || null;
          if (!annotationId) {
            resetAnnotationHover(view);
            return;
          }
          setAnnotationHover(view, annotationId, commentId);
        },
        blur: (view) => {
          resetAnnotationHover(view);
        },
        pointerleave: (view) => {
          resetAnnotationHover(view);
        }
      }
    }
  });
};

export { AnnotationPlugin, AnnotationPluginKey, findAnnotationPosition };
//# sourceMappingURL=AnnotationPlugin.js.map
